import { useState } from 'react';

import { CombinedGraphQLErrors } from '@apollo/client';
import { Box, TextInput, useMedia } from '@opendoor/bricks-next';
import { genericErrorText } from 'platform/components/AccountPreferences';
import { useUpdatePassword } from 'platform/graphql/useCustomerDetails';
import navigationHelpers from 'platform/navigationHelpers';
import { useObservability } from 'platform/observability';

import { SectionHeader } from './SectionHeader';

/**
 * Displays password and allowsw user to update the same
 *
 */
export const PasswordUpdateContainer = () => {
  // Password Info
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [currentPasswordError, setCurrentPasswordError] = useState<string>('');

  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordError, setNewPasswordError] = useState<string>('');
  const { updatePassword, loading: updatingPassword } = useUpdatePassword();
  const { largerThanSM } = useMedia();

  const { trackError } = useObservability();

  const inputSize = largerThanSM ? 'medium' : 'small';

  const isFormValid = () => {
    let valid = true;
    if (newPassword.length > 0 && newPassword.length < 5) {
      setNewPasswordError('Your new password does not meet requirements');
      valid = false;
    }

    if (newPassword.length > 1 && currentPassword.length < 1) {
      setCurrentPasswordError('Confirm your current password to change your password');
      valid = false;
    }

    return valid;
  };

  const redirectToLogin = () => {
    const currentUrl = new URL(window.location.href);
    navigationHelpers.hardNavReplace(
      `/login?updatedPassword=true&redirectTo=${encodeURIComponent(currentUrl.toString())}`,
    );
  };

  const updateLoginCredentials = async () => {
    if (!isFormValid()) return;

    //No Updates to Password
    if (newPassword.length < 1) {
      return;
    }

    try {
      const { error } = await updatePassword({
        variables: {
          currentPassword,
          newPassword,
        },
      });

      if (error) {
        if (
          CombinedGraphQLErrors.is(error) &&
          error.errors.some((error) => error.message.includes('incorrect current password'))
        ) {
          setCurrentPasswordError('Your current password is incorrect.');
        } else {
          setCurrentPasswordError(genericErrorText);
        }
      } else {
        redirectToLogin();
      }
    } catch {
      setCurrentPasswordError(genericErrorText);

      trackError('Error Updating Password');
    }
  };

  return (
    <Box testID="password-container" gap="$12x">
      <SectionHeader
        header="Password"
        saveButtonId="update-password"
        analyticsName="update-password"
        description="Your password must be 5 to 128 characters long"
        inputSize={inputSize}
        onSave={() => void updateLoginCredentials()}
        updating={updatingPassword}
      />
      <Box
        $largerThanSM={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: '$44x',
        }}
        gap="$12x"
      >
        <Box flex={1}>
          <TextInput
            id="current-password-field"
            size={inputSize}
            title="Confirm current password"
            value={currentPassword}
            onChangeText={(value) => {
              setCurrentPassword(value);
              setCurrentPasswordError('');
            }}
            errorText={currentPasswordError}
            textContentType="password"
          />
        </Box>
        <Box flex={1}>
          <TextInput
            id="new-password-field"
            size={inputSize}
            title="Enter new password"
            value={newPassword}
            onChangeText={(value) => {
              setNewPassword(value);
              setNewPasswordError('');
            }}
            errorText={newPasswordError}
            textContentType="password"
          />
        </Box>
      </Box>
    </Box>
  );
};
